import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { createPrevisaoInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreatePrevisaoInput = z.infer<typeof createPrevisaoInput>;

interface PrevisaoOrcamentariaTableProps {
  eventoId: string;
}

export function PrevisaoOrcamentariaTable({ eventoId }: PrevisaoOrcamentariaTableProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.previsaoOrcamentaria.listPrevisoes.useQuery({ eventoId, page, limit: 20 });
  const previsoes = data?.items;
  const { data: gabaritoData } = trpc.livroCaixa.listGabarito.useQuery({ eventoId, page: 1, limit: 500 });
  const gabarito = gabaritoData?.items;
  const { data: resumo } = trpc.previsaoOrcamentaria.getResumoPrevisao.useQuery({ eventoId });

  const invalidateAll = () => {
    utils.previsaoOrcamentaria.listPrevisoes.invalidate();
    utils.previsaoOrcamentaria.getResumoPrevisao.invalidate({ eventoId });
    utils.previsaoOrcamentaria.getComparativo.invalidate({ eventoId });
  };

  const addMutation = trpc.previsaoOrcamentaria.addPrevisao.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseModal();
    },
  });

  const updateMutation = trpc.previsaoOrcamentaria.updatePrevisao.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseModal();
    },
  });

  const removeMutation = trpc.previsaoOrcamentaria.removePrevisao.useMutation({
    onSuccess: () => {
      invalidateAll();
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreatePrevisaoInput>({
    resolver: zodResolver(createPrevisaoInput),
    defaultValues: { eventoId },
  });

  const onSubmit = (data: CreatePrevisaoInput) => {
    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        data: data.data,
        contaGabaritoId: data.contaGabaritoId,
        descricao: data.descricao,
        valor: data.valor,
      });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleOpenModal = () => {
    setEditingId(null);
    reset({
      eventoId,
      data: new Date().toISOString().split('T')[0],
      contaGabaritoId: '',
      descricao: '',
      valor: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (previsao: NonNullable<typeof previsoes>[number]) => {
    setEditingId(previsao.id);
    reset({
      eventoId,
      data: previsao.data,
      contaGabaritoId: previsao.contaGabaritoId,
      descricao: previsao.descricao,
      valor: previsao.valor,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    reset();
  };

  const handleRemove = (id: string) => {
    if (window.confirm('Tem certeza que deseja remover esta previsão?')) {
      removeMutation.mutate({ id });
    }
  };

  const formatCurrency = (value: string | number | null) => {
    if (!value) return 'R$ 0,00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const formatDate = (date: string) => {
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Receitas Previstas</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(resumo?.totalReceitas || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Despesas Previstas</p>
            <p className="text-xl font-bold text-red-600">{formatCurrency(resumo?.totalDespesas || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Saldo Previsto</p>
            <p className={`text-xl font-bold ${(resumo?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(resumo?.saldo || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{previsoes?.length || 0} previsões</p>
        <Button onClick={handleOpenModal} disabled={!canEdit || !gabarito || gabarito.length === 0}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Previsão
        </Button>
      </div>

      {!gabarito || gabarito.length === 0 ? (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center">
          <p className="text-sm text-yellow-800">
            Configure o Gabarito de Contas antes de criar previsões.
          </p>
        </div>
      ) : null}

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {previsoes && previsoes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Nº</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead className="w-12">Cód</TableHead>
                  <TableHead>Natureza</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previsoes.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-gray-500">{p.numero}</TableCell>
                    <TableCell>{formatDate(p.data)}</TableCell>
                    <TableCell className="font-medium">{p.contaNome}</TableCell>
                    <TableCell className="text-gray-500">{p.contaCodigo}</TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        p.natureza === 'RECEITA'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {p.natureza === 'RECEITA' ? 'Receita' : 'Despesa'}
                      </span>
                    </TableCell>
                    <TableCell>{p.descricao}</TableCell>
                    <TableCell className={`text-right font-medium ${
                      p.natureza === 'RECEITA' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {p.natureza === 'DESPESA' && '-'}{formatCurrency(p.valor)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(p)}
                          disabled={!canEdit}
                          className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                          <Pencil className="h-4 w-4 text-gray-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(p.id)}
                          disabled={!canEdit || removeMutation.isPending}
                          className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="bg-gray-50 font-semibold">
                  <TableCell colSpan={6} className="text-right">Totais:</TableCell>
                  <TableCell className="text-right">
                    <div className="text-green-600">{formatCurrency(resumo?.totalReceitas || 0)}</div>
                    <div className="text-red-600">-{formatCurrency(resumo?.totalDespesas || 0)}</div>
                    <div className={`border-t border-gray-300 pt-1 ${(resumo?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(resumo?.saldo || 0)}
                    </div>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <p>Nenhuma previsão registrada</p>
            </div>
          )}
          {data && (
            <div className="px-4 pb-2">
              <Pagination meta={data.meta} onPageChange={setPage} itemLabel="previsões" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingId ? 'Editar Previsão' : 'Nova Previsão'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register('eventoId')} />

          <div className="grid grid-cols-2 gap-4">
            <Input label="Data" type="date" {...register('data')} error={errors.data?.message} />
            <Input label="Valor" type="number" step="0.01" {...register('valor')} error={errors.valor?.message} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Conta</label>
            <select
              {...register('contaGabaritoId')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Selecione uma conta</option>
              <optgroup label="Receitas">
                {gabarito?.filter((g) => g.natureza === 'RECEITA').map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.codigo} - {g.nome}{g.childCount > 0 ? ' [Agrupadora]' : ''}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Despesas">
                {gabarito?.filter((g) => g.natureza === 'DESPESA').map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.codigo} - {g.nome}{g.childCount > 0 ? ' [Agrupadora]' : ''}
                  </option>
                ))}
              </optgroup>
            </select>
            {errors.contaGabaritoId && (
              <p className="mt-1 text-sm text-red-600">{errors.contaGabaritoId.message}</p>
            )}
          </div>

          <Input label="Descrição" {...register('descricao')} error={errors.descricao?.message} />

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>Cancelar</Button>
            <Button type="submit" isLoading={addMutation.isPending || updateMutation.isPending}>
              {editingId ? 'Salvar' : 'Adicionar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
